const colorOptions = (t) => ({
  green: {
    value: 'green',
    label: t('BoardConfig.colors.green'),
    color: '#2d8e44',
  },
  white: {
    value: 'white',
    label: t('BoardConfig.colors.white'),
    color: '#ffffff',
  },
  black: {
    value: 'black',
    label: t('BoardConfig.colors.black'),
    color: '#1a1a1a',
  },
  red: {
    value: 'red',
    label: t('BoardConfig.colors.red'),
    color: '#c41e3a',
  },
  blue: {
    value: 'blue',
    label: t('BoardConfig.colors.blue'),
    color: '#0066cc',
  },
});

const silkscreenColorOptions = (t) => ({
  white: {
    value: 'white',
    label: t('BoardConfig.colors.white'),
    color: '#ffffff',
  },
  black: {
    value: 'black',
    label: t('BoardConfig.colors.black'),
    color: '#1a1a1a',
  },
});

export const buildBoardConfigOptions = (t) => {
  const soldermaskColors = colorOptions(t);
  const silkscreenColors = silkscreenColorOptions(t);

  return [
    {
      key: 'pcbThickness',
      title: t('BoardConfig.pcbThickness'),
      options: [
        { value: '0.8mm', label: '0.8mm' },
        { value: '1.6mm', label: '1.6mm' },
      ],
    },
    {
      key: 'surfaceFinish',
      title: t('BoardConfig.surfaceFinish'),
      options: [
        { value: 'HASL', label: t('BoardConfig.surfaceFinishes.hasl') },
        { value: 'ENIG', label: t('BoardConfig.surfaceFinishes.enig') },
      ],
    },
    {
      key: 'copperThickness',
      title: t('BoardConfig.copperThickness'),
      options: [
        { value: '35um', label: '35 &micro;m' },
        { value: '70um', label: '70 &micro;m' },
      ],
    },
    {
      key: 'soldermaskColor',
      title: t('BoardConfig.soldermaskColor'),
      showColorIndicator: true,
      options: [
        soldermaskColors.green,
        soldermaskColors.white,
        soldermaskColors.black,
        soldermaskColors.red,
        soldermaskColors.blue,
      ],
    },
    {
      key: 'silkscreenColor',
      title: t('BoardConfig.silkscreenColor'),
      showColorIndicator: true,
      options: [
        silkscreenColors.white,
        silkscreenColors.black,
      ],
    },
  ];
};

export const boardConfigDisabledRules = (projectDetails) => {
  const rules = [];

  // Disable 0.8mm HASL
  rules.push({
    option: 'surfaceFinish',
    value: 'HASL',
    conditions: {
      pcbThickness: '0.8mm',
    },
    message: 'haslWithThinPcbThickness',
  });

  // Disable Color Rules
  rules.push({
    option: 'silkscreenColor',
    value: 'white',
    conditions: {
      soldermaskColor: 'white',
    },
    message: 'ensureContrast',
  });
  rules.push({
    option: 'silkscreenColor',
    value: 'black',
    conditions: {
      soldermaskColor: [
        'green',
        'black',
        'red',
        'blue'
      ],
    },
    message: 'ensureContrast',
  });

  // Board specific rules
  if (projectDetails?.rating === 'complex') {
      // 2L Complex must be mfgrd in ENIG
    rules.push({
      option: 'surfaceFinish',
      value: 'HASL',
      message: 'haslNotSupportedComplex',
    });
  }

  return rules;
};

const isRuleConditionMatch = (selectedValue, conditionValue) => {
  if (Array.isArray(conditionValue)) return conditionValue.includes(selectedValue);
  return selectedValue === conditionValue;
};

export const matchingBoardConfigDisabledRules = ({
  projectDetails,
  configOption,
  optionValue,
  selectedBoardConfig = {},
}) => {
  const disabledRules = boardConfigDisabledRules(projectDetails);

  return disabledRules.filter((rule) => {
    if (rule?.option !== configOption || rule?.value !== optionValue) return false;

    const conditions = rule?.conditions || {};
    const conditionEntries = Object.entries(conditions);
    if (conditionEntries.length === 0) return true;

    return conditionEntries.some(([conditionKey, conditionValue]) =>
      isRuleConditionMatch(selectedBoardConfig?.[conditionKey], conditionValue)
    );
  });
};

export const isBoardConfigDisabled = ({
  projectDetails,
  configOption,
  optionValue,
  selectedBoardConfig = {},
}) =>
  matchingBoardConfigDisabledRules({
    projectDetails,
    configOption,
    optionValue,
    selectedBoardConfig,
  }).length > 0;

export const boardConfigDisabledMessage = ({
  projectDetails,
  configOption,
  optionValue,
  selectedBoardConfig = {},
  t,
  separator = '<br>---<br>',
}) => {
  const disabledRules = matchingBoardConfigDisabledRules({
    projectDetails,
    configOption,
    optionValue,
    selectedBoardConfig,
  });

  const translatedMessages = disabledRules.map((rule) => t(`BoardConfigOptions.${rule.message}`));
  return translatedMessages.join(separator);
};
